import React from 'react';
import { BsFillHouseUpFill } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa6';
import { MdEnergySavingsLeaf, MdSavings } from 'react-icons/md';
import { GetStarted } from '../get-started';

const Hero = () => {
  return (
    <>
      <div className="max-x-7xl w-full bg-[#DFF2EF] pb-16 pt-9 sm:px-6 xl:px-44">
        <div className="mx-auto flex flex-col-reverse items-center gap-12 px-4 md:flex-row md:px-2 lg:max-w-7xl">
          <div className="order-2 flex flex-1 flex-col gap-4 sm:w-[80%] md:order-1 md:gap-6">
            <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-[64px] lg:leading-[80px]">
              Home energy efficiency{' '}
              <span className="font-kalam text-nowrap bg-gradient-to-l from-[#299B8D] via-[#F4A261] to-[#E76F51] bg-clip-text text-[24px] font-bold lowercase text-transparent md:text-[36px] lg:text-[48px] xl:leading-[80px]">
                made easy!
              </span>
            </h1>
            <p className="font-jost max-w-xl leading-[28px] lg:text-xl lg:leading-[38px]">
              Why waste time searching for various installers, assessors, and finance solutions? We have it all under
              one roof.
            </p>
            {/* IMAGE (MOBILE ONLY) */}
            <div className="flex justify-center md:hidden">
              <div className="flex w-[60%] items-center justify-center">
                <img src="/images/hero-illustration.png" alt="Energy efficient home" className="w-full" />
              </div>
            </div>
            <ul className="flex flex-col gap-3">
              <li className="flex items-center gap-4">
                <FaCheck size="24px" className="text-[#2A9C8E]" />
                <span className="font-jost font-medium leading-[150%]">Save money on your energy bills</span>
              </li>
              <li className="flex items-center gap-4">
                <FaCheck size="24px" className="text-[#2A9C8E]" />
                <span className="font-jost font-medium leading-[150%]">Increase the value of your home</span>
              </li>
              <li className="flex items-center gap-4">
                <FaCheck size="24px" className="text-[#2A9C8E]" />
                <span className="font-jost font-medium leading-[150%]">Reduce your home’s carbon emissions</span>
              </li>
            </ul>
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              <GetStarted />
            </div>
          </div>
          <div className="order-1 hidden flex-1 justify-center md:order-2 md:flex">
            <div className="flex items-center justify-center">
              <img src="/images/hero-illustration.png" alt="Energy efficient home" className="w-full max-w-md" />
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl flex-row flex-wrap items-center justify-around gap-[45px] px-6 py-9">
        <img src="/images/logos/mcs-logo.png" alt="MCS Logo" className="max-h-[85px] max-w-[250px]" />
        <img src="/images/logos/trustmark-logo.png" alt="Trustmark Logo" className="max-h-[85px] max-w-[250px]" />
        <img src="/images/logos/niceic-logo.png" alt="Niceic Logo" className="max-h-[85px] max-w-[250px]" />
        <img src="/images/logos/iaa-logo.webp" alt="IAA Logo" className="max-h-[85px] max-w-[250px]" />
        <img src="/images/logos/hies-logo.png" alt="Hies Logo" className="max-h-[85px] max-w-[250px]" />
      </div>
      <div className="lg:px-4 lg:py-9">
        <div className="mx-auto max-w-7xl text-center">
          <h2 className="px-4 text-2xl font-bold lg:text-5xl">
            Real{' '}
            <span className="font-kalam bg-gradient-to-l from-[#84AF7D] via-[#289A8C] to-[#214D55] bg-clip-text text-[32px] font-bold lowercase leading-[48px] text-transparent lg:text-[64px] lg:leading-[80px]">
              benefits
            </span>{' '}
            you can feel
          </h2>
          <div className="grid grid-cols-1 gap-2 px-4 pt-4 sm:grid-cols-2 sm:gap-6 md:grid-cols-3 lg:pb-2 lg:pt-9">
            <div className="rounded-lg bg-[#2a9c8e14] p-6 text-left">
              <div className="flex flex-row items-start justify-between lg:gap-4">
                <p className="font-jost mb-5 text-xl font-medium sm:max-w-32 lg:text-2xl xl:max-w-52">
                  Save money on your energy bills
                </p>
                <MdSavings className="size-[80px] text-[#A2D6CF]" />
              </div>
              <p className="leading-[28px] lg:leading-[30px]">
                Energy-efficient upgrades can lower your annual bills by <strong>£300–£500</strong>, depending on your
                home and improvements. That’s money back in your pocket — year after year.
              </p>
            </div>
            <div className="rounded-lg bg-[#2a9c8e14] p-6 text-left">
              <div className="flex flex-row items-start justify-between lg:gap-4">
                <p className="font-jost mb-5 text-xl font-medium sm:max-w-32 lg:text-2xl xl:max-w-52">
                  Increase the value of your home
                </p>
                <BsFillHouseUpFill className="size-[72px] text-[#A2D6CF]" />
              </div>
              <p className="leading-[28px] lg:leading-[30px]">
                Homes with improved energy performance can see an average increase in value of{' '}
                <strong>up to £10,000</strong>. Buyers are looking for energy-efficient, future-ready homes — and an
                upgraded EPC rating can make your property stand out.
              </p>
            </div>
            <div className="rounded-lg bg-[#2a9c8e14] p-6 text-left">
              <div className="flex flex-row items-start justify-between lg:gap-4">
                <p className="font-jost mb-5 text-xl font-medium sm:max-w-32 lg:text-2xl xl:max-w-52">
                  Reduce your home’s carbon emissions
                </p>
                <MdEnergySavingsLeaf className="size-[80px] text-[#A2D6CF]" />
              </div>
              <p className="leading-[28px] lg:leading-[30px]">
                The average retrofit cuts household carbon emissions by{' '}
                <strong>1.5 to 3 tonnes of CO₂ each year</strong>. That’s the equivalent of planting{' '}
                <strong>70+ trees annually</strong> - and your future self (and the planet) will thank you for it.
              </p>
            </div>
          </div>
          <div className="px-4 pb-4 pt-2 sm:p-4">
            <div className="flex w-full flex-col items-center gap-2 rounded-lg bg-[#2a9c8e14] px-4 py-2 text-left md:flex-row lg:gap-6">
              <p className="font-jost text-xl font-medium md:w-[80%] lg:w-full lg:text-2xl">
                Ready to improve your home’s energy efficiency?
              </p>
              <div className="w-full">
                <GetStarted />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;
